import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import GMap from '@/components/ui/GMap';
import { Device } from '@/generated/graphql';
import { execute } from '@/graphql/execute';
import { convertCoordToGoogleMapsFormat } from '@/utils/map';
import { useQuery } from '@tanstack/react-query';
import centroid from '@turf/centroid';
import { useEffect, useState } from 'react';
import { GeoJSONGeometry } from 'wellknown';
import { snZoneQuery } from '../graphql/query';

import { Separator } from '@/components/ui/Separator';
import { ClientZone } from '@/models/zone.models';
import { DialogDescription } from '@radix-ui/react-dialog';
import * as wellknown from 'wellknown';

type ZonePickerDeviceDialogProps = {
  open: boolean;
  currentZone?: string;
  onToggleOpen: (zoneKey?: string) => void;
  device: Device;
};

export function ZonePickerDeviceDialog({
  open,
  device,
  currentZone,
  onToggleOpen,
}: ZonePickerDeviceDialogProps) {
  const [mapCenterChange, setMapCenterChange] = useState<google.maps.LatLng>();
  const [mapCenter, setMapCenter] = useState<
    google.maps.LatLng | google.maps.LatLngLiteral
  >();
  const [userSelectedKey, setUserSelectedKey] = useState<string>();
  const [zoneFetchRadius, setZoneFetchRadius] = useState<number>(50);

  const [mapDisplayItems, setMapDisplayItems] = useState<ClientZone[]>([]);
  const { data: snZoneData } = useQuery({
    queryKey: ['SnZones', device, mapCenterChange, zoneFetchRadius],
    queryFn: () =>
      execute({
        query: snZoneQuery,
        variables: {
          id: device.id,
          distanceWithinMeters: zoneFetchRadius,
          longitude: mapCenterChange!.lng(),
          latitude: mapCenterChange!.lat(),
        },
      }),
    select: (data) => data,
    retry: false,
    staleTime: 15000,
    enabled: !!mapCenterChange && !!device,
    meta: { suppressErrorToast: true },
  });

  useEffect(() => {
    let mapItems: ClientZone[] = [];
    if (device?.site) {
      mapItems = [
        {
          key: 'site',
          geometry: device.site.geometry,
        },
      ];
    }
    if (snZoneData) {
      const zones = snZoneData?.snZones?.map((zone) => {
        return {
          key: zone.zoneKey,
          geometry: wellknown.parse(zone?.polygon) as GeoJSONGeometry,
        };
      });
      mapItems = [...mapItems, ...zones];
    }
    setMapDisplayItems(mapItems);
  }, [snZoneData, device]);

  useEffect(() => {
    if (device?.site?.geometry) {
      const centerPoint = centroid(device.site.geometry as GeoJSONGeometry);
      const centerLatLng = convertCoordToGoogleMapsFormat(
        centerPoint.geometry?.coordinates,
      );
      setMapCenter(centerLatLng);
    }
  }, [device]);

  const handleToggleOpenState = () => {
    onToggleOpen();
  };

  const handleMapCenterChange = (mapCenter: google.maps.LatLng) => {
    setMapCenterChange(mapCenter);
  };

  return (
    <Dialog open={open} onOpenChange={handleToggleOpenState}>
      <DialogContent className="sm:max-w-[90vw] xl:max-w-[80vw]">
        <DialogHeader>
          <DialogTitle>Select Zone</DialogTitle>
          <DialogDescription />
        </DialogHeader>
        <div className="flex flex-col space-y-1">
          <div className="flex h-[30px] items-center space-x-2">
            <div className="text-muted-foreground text-md w-[120px] font-normal tracking-tight">
              Current Zone
            </div>
            <div className="text-lg font-semibold tracking-tight">
              {currentZone}
            </div>
          </div>
          <div className="flex h-[30px] items-center space-x-2">
            <div className="text-muted-foreground text-md w-[120px] font-normal tracking-tight">
              Selected Zone
            </div>
            <span className="text-lg font-semibold tracking-tight">
              {userSelectedKey}
            </span>
          </div>
        </div>
        <Separator orientation="horizontal" />

        <div className="grid h-[60vh] grid-cols-12 gap-4">
          <div className="card bg-muted/60 col-span-5 flex h-[100%] space-y-2 overflow-auto rounded px-2 py-4">
            <div className="flex-1 space-y-1">
              {mapDisplayItems
                .filter((item) => item.key !== 'site')
                .map((item, index) => (
                  <button
                    onClick={() => {
                      setUserSelectedKey(item.key);
                    }}
                    key={index}
                    className="hover:bg-secondary-foreground hover:text-secondary w-full rounded px-3 py-1.5 text-left text-sm font-medium hover:cursor-pointer"
                  >
                    {item.key}
                  </button>
                ))}
            </div>
          </div>
          <div className="col-span-7">
            <GMap
              centerCircleRadius={zoneFetchRadius}
              className="h-full"
              center={mapCenter}
              displayItems={mapDisplayItems}
              onCenterChange={handleMapCenterChange}
              onCenterCircleRadiusChange={(radius) =>
                setZoneFetchRadius(radius)
              }
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={() => {
              onToggleOpen(userSelectedKey);
            }}
            variant={'default'}
            size={'sm'}
            className="w-50"
            disabled={!userSelectedKey}
          >
            Select
          </Button>
          <Button
            onClick={() => {
              onToggleOpen();
            }}
            variant={'outline'}
            size={'sm'}
            className="w-50"
          >
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
